@if (isLoading() && !orders().length) {
  <cmt-account-orders-skeleton />
} @else {
  @if (error(); as err) {
    <div class="orders-error">
      {{ err }}
    </div>
  }

  <div class="orders-heading">
    <h1>
      {{ 'global.my_orders' | translate }}
      @if (orders().length) {
        <span class="orders-count">({{ orders().length }})</span>
      }
    </h1>
  </div>

  @if (orders().length) {
    <form
      class="orders-toolbar"
      role="search"
      (submit)="$event.preventDefault(); onSearch(searchInput())"
    >
      <label
        class="orders-search-label"
        for="orders-search"
      >
        {{ 'account_orders.search_label' | translate }}
      </label>
      <div class="orders-search-row">
        <div class="orders-search">
          <tc-icon name="search" />
          <input
            id="orders-search"
            class="orders-search-input"
            [placeholder]="'account_orders.search_placeholder' | translate"
            [value]="searchInput()"
            type="text"
            (input)="searchInput.set($any($event.target).value)"
            #searchField
          >
          @if (searchInput()) {
            <button
              class="search-clear"
              [attr.aria-label]="'account_orders.clear_search' | translate"
              type="button"
              (click)="onSearch(''); searchField.focus()"
            >
              <tc-icon name="close" />
            </button>
          }
        </div>
        <tc-button
          class="orders-search-submit"
          buttonType="submit"
          leftIconName="search"
          isBold
        >
          {{ 'global.search' | translate }}
        </tc-button>
      </div>
    </form>
  } @else if (!isLoading() && !error()) {
    <div class="no-orders">
      <tc-icon name="orders" />
      {{ 'account_orders.no_orders' | translate }}
    </div>
  }

  @if (searchQuery()) {
    <p
      class="search-results"
      role="status"
    >
      {{ 'account_orders.results' | translate: { count: filteredOrders().length } }}
    </p>
  }
  @if (orders().length && !filteredOrders().length) {
    <div class="no-orders">
      <tc-icon name="search" />
      {{ 'account_orders.nothing_found' | translate }}
    </div>
  }

  @for (order of pageOrders(); track order.id) {
    <article
      class="order"
      [class.order-expanded]="expandedOrderIds().has(order.id)"
    >
      <button
        class="order-header"
        [attr.aria-expanded]="expandedOrderIds().has(order.id)"
        [attr.aria-controls]="'order-details-' + order.id"
        type="button"
        (click)="toggleOrderExpand(order.id, $event)"
      >
        <span class="order-title">
          <span class="order-heading">
            <span class="order-number">
              {{ 'account_orders.order' | translate }} №{{ order.id }}
            </span>
            <span
              class="order-status"
              [class.order-status-danger]="statusTone(order) === 'danger'"
              [class.order-status-success]="statusTone(order) === 'success'"
              [class.order-status-warning]="statusTone(order) === 'warning'"
            >
              {{ order.status }}
            </span>
          </span>
          <time
            class="order-date"
            [attr.datetime]="order.createdAt"
          >
            {{ order.createdAt | date:'mediumDate' : undefined : languageService.getCurrentLocale() }} {{ order.createdAt | date:'HH:mm' }}
          </time>
        </span>
        <span class="order-price">
          {{ order.prices.totalCost }} {{ 'global.uah' | translate }}
        </span>
        <span
          class="order-expand"
          [class.order-expand-expanded]="expandedOrderIds().has(order.id)"
        >
          <span class="expand-label">{{ (expandedOrderIds().has(order.id) ? 'account_orders.hide_details' : 'account_orders.details') | translate }}</span>
          <tc-icon
            name="chevron-down"
            aria-hidden="true"
          />
        </span>
      </button>
      @if (order.isAwaitingPayment) {
        <div class="order-payment">
          <div class="order-payment-due">
            {{ 'payment_options.due' | translate }}
            <strong>{{ order.dueAmount }} {{ 'global.uah' | translate }}</strong>
          </div>
          <cmt-order-payment-options
            layout="buttons"
            [order]="order"
          />
        </div>
      }
      @if (!expandedOrderIds().has(order.id)) {
        <button
          class="order-preview"
          [attr.aria-expanded]="expandedOrderIds().has(order.id)"
          [attr.aria-controls]="'order-details-' + order.id"
          type="button"
          (click)="toggleOrderExpand(order.id)"
        >
          <span class="order-thumbs">
            @for (item of order.items.slice(0, previewImagesCount); track item.productId) {
              <img
                class="order-img"
                [src]="item.imageUrl"
                [alt]="item.name"
                loading="lazy"
              >
            }
            @if (order.items.length > previewImagesCount) {
              <span class="order-thumbs-more">+{{ order.items.length - previewImagesCount }}</span>
            }
          </span>
          <span class="order-preview-meta">
            <span class="preview-count">{{ 'account_orders.items_count' | translate: { count: order.items.length } }}</span>
            <span>{{ order.shipment.shippingMethodDescription }}</span>
          </span>
        </button>
      }
      @if (expandedOrderIds().has(order.id)) {
        <div
          class="order-content"
          [id]="'order-details-' + order.id"
        >
          <cmt-order-details [order]="order">
            <div class="order-actions">
              <tc-button
                class="order-reorder"
                [isLoading]="reorderingOrderId() === order.id"
                buttonSize="small"
                leftIconName="repeat"
                isTransparent
                isBold
                (click)="reorder(order)"
              >
                {{ 'account_orders.reorder' | translate }}
              </tc-button>
            </div>
          </cmt-order-details>
        </div>
      }
    </article>
  }

  @if (pagesCount() > 1) {
    <nav class="orders-pager">
      <button
        class="orders-pager-item"
        [attr.aria-label]="'account_orders.prev' | translate"
        [disabled]="page() === 1"
        type="button"
        (click)="goToPage(page() - 1)"
      >
        ‹
      </button>
      @for (pageNumber of pageNumbers(); track pageNumber) {
        <button
          class="orders-pager-item"
          [class.orders-pager-item-current]="pageNumber === page()"
          [attr.aria-current]="pageNumber === page() ? 'page' : null"
          type="button"
          (click)="goToPage(pageNumber)"
        >
          {{ pageNumber }}
        </button>
      }
      <button
        class="orders-pager-item"
        [attr.aria-label]="'account_orders.next' | translate"
        [disabled]="page() === pagesCount()"
        type="button"
        (click)="goToPage(page() + 1)"
      >
        ›
      </button>
    </nav>
  }
}
